/**
 * Org structure schemas — Divisions, Departments, Roles, Publications,
 * Assignments (spec §3.3, §3.4). Mirrors:
 *   - chaos.divisions
 *   - chaos.departments
 *   - chaos.roles (org_id NULL = global/template)
 *   - chaos.role_publications
 *   - chaos.role_assignments
 *
 * Convention same as orgs.ts: camelCase wire shape, ISO timestamps,
 * uuid strings.
 */
import { z } from 'zod';

// ── Role kind / autonomy / permission_mode (CHECK constraints) ───────
export const roleKinds = ['agent', 'human', 'hybrid'] as const;
export const roleKindSchema = z.enum(roleKinds);
export type RoleKind = z.infer<typeof roleKindSchema>;

export const cliAgents = ['claude', 'codex', 'gemini'] as const;
export const cliAgentSchema = z.enum(cliAgents);
export type CliAgent = z.infer<typeof cliAgentSchema>;

// M-X16: defaults that travel with a role and pre-fill the chat
// composer picker when the role joins a session. Mirrors the vendor /
// effort vocabularies enforced by migration 0019 / 0025. `defaultModel`
// stays free-form because the model catalog (MODEL_PRESETS_BY_VENDOR
// in apps/chaos-ui) grows independent of migrations.
export const vendors = ['anthropic', 'openai', 'google'] as const;
export const vendorSchema = z.enum(vendors);
export type Vendor = z.infer<typeof vendorSchema>;

// 2026-10-04: 'ultra' is OpenAI's own top level (codex 0.160.0; migration 0391).
export const efforts = ['minimal', 'low', 'medium', 'high', 'xhigh', 'max', 'ultracode', 'ultra'] as const;
export const effortSchema = z.enum(efforts);
export type Effort = z.infer<typeof effortSchema>;

export const permissionModes = ['default', 'acceptEdits', 'bypassPermissions', 'plan'] as const;
export const permissionModeSchema = z.enum(permissionModes);
export type PermissionMode = z.infer<typeof permissionModeSchema>;

// Spec §3.3 wording uses HITL/HOTL/HOOL/Auto. Existing kanban.ts uses
// 'agent_only' instead of 'Auto'; we accept the spec's literal here.
export const roleAutonomyLevels = ['HITL', 'HOTL', 'HOOL', 'Auto'] as const;
export const roleAutonomyLevelSchema = z.enum(roleAutonomyLevels);
export type RoleAutonomyLevel = z.infer<typeof roleAutonomyLevelSchema>;

// ── Division ─────────────────────────────────────────────────────────
export const divisionSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  slug: z.string().min(1).max(120),
  displayName: z.string().min(1).max(200),
  position: z.number(),
  vfp: z.string().nullable(),
  instructions: z.string().nullable(),
  metrics: z.unknown().nullable(),
  icon: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Division = z.infer<typeof divisionSchema>;

export const createDivisionSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  displayName: z.string().min(1).max(200),
  position: z.number().optional(),
  vfp: z.string().max(4000).nullable().optional(),
  instructions: z.string().max(100_000).nullable().optional(),
  metrics: z.unknown().nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type CreateDivisionInput = z.infer<typeof createDivisionSchema>;

export const updateDivisionSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .optional(),
  displayName: z.string().min(1).max(200).optional(),
  position: z.number().optional(),
  vfp: z.string().max(4000).nullable().optional(),
  instructions: z.string().max(100_000).nullable().optional(),
  metrics: z.unknown().nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type UpdateDivisionInput = z.infer<typeof updateDivisionSchema>;

export const reorderDivisionSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: z.number(),
      }),
    )
    .min(1),
});
export type ReorderDivisionInput = z.infer<typeof reorderDivisionSchema>;

// ── Department ───────────────────────────────────────────────────────
export const departmentSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  divisionId: z.string().uuid().nullable(),
  parentDeptId: z.string().uuid().nullable(),
  slug: z.string(),
  displayName: z.string(),
  position: z.number(),
  vfp: z.string(),
  instructions: z.string().nullable(),
  metrics: z.unknown().nullable(),
  icon: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Department = z.infer<typeof departmentSchema>;

export const createDepartmentSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  displayName: z.string().min(1).max(200),
  divisionId: z.string().uuid().nullable().optional(),
  parentDeptId: z.string().uuid().nullable().optional(),
  position: z.number().optional(),
  vfp: z.string().min(1).max(4000),
  instructions: z.string().max(100_000).nullable().optional(),
  metrics: z.unknown().nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;

export const updateDepartmentSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .optional(),
  displayName: z.string().min(1).max(200).optional(),
  divisionId: z.string().uuid().nullable().optional(),
  parentDeptId: z.string().uuid().nullable().optional(),
  position: z.number().optional(),
  vfp: z.string().min(1).max(4000).optional(),
  instructions: z.string().max(100_000).nullable().optional(),
  metrics: z.unknown().nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;

export const reorderDepartmentSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: z.number(),
      }),
    )
    .min(1),
});
export type ReorderDepartmentInput = z.infer<typeof reorderDepartmentSchema>;

// ── Role ─────────────────────────────────────────────────────────────
export const roleViewScopeSchema = z
  .object({
    projects: z.array(z.string().uuid()).optional(),
    boards: z.array(z.string().uuid()).optional(),
    cardsOwnedOnly: z.boolean().optional(),
    crossOrg: z.boolean().optional(),
  })
  .passthrough();
export type RoleViewScope = z.infer<typeof roleViewScopeSchema>;

export const roleSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid().nullable(),
  departmentId: z.string().uuid().nullable(),

  slug: z.string(),
  displayName: z.string(),
  vfp: z.string(),
  kind: roleKindSchema,

  cliAgent: z.string().nullable(),
  systemPrompt: z.string().nullable(),
  permissionMode: z.string().nullable(),
  allowedTools: z.array(z.string()).nullable(),
  defaultAutonomy: z.string().nullable(),

  viewScope: z.unknown().nullable(),
  fullVpsAccess: z.boolean(),
  // `extraMounts: z.array(z.string()).nullable()` lived here until 0169
  // (2026-08-01). The API accepted it, the database stored it, and no consumer
  // ever read it — the role editor had no field for it, and no spawn site
  // consulted it. It is gone rather than implemented: a free host path from an
  // operator, bound straight into a container, defeats SPEC-03 outright. The
  // shape a future implementation must take (catalog slugs, `:ro`, role-gated)
  // is written down in docs/MULTI_AGENT_ORCHESTRATION_SPEC.md.

  reportsToRoleId: z.string().uuid().nullable(),
  canAutoApprove: z.unknown().nullable(),

  instructions: z.string().nullable(),
  icon: z.string().nullable(),
  // M-X12: text color for the role-chip in the Multi-Role composer.
  // Hex string ("#rgb" or "#rrggbb"). Default = '#64748b' (slate-500).
  // Non-optional / non-default so the inferred type stays
  // `string | null` (and not `string | null | undefined`); the
  // backend always emits a value because migration 0022 set a column
  // default.
  textColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/)
    .nullable(),
  // M-X16: default vendor/model/effort that pre-fill the chat picker
  // when this role joins a session. NULL = no preference. Vendor +
  // effort are constrained by CHECK in migration 0025; model is free-
  // form (the catalog is UI-side).
  defaultVendor: vendorSchema.nullable(),
  defaultModel: z.string().max(120).nullable(),
  defaultEffort: effortSchema.nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  // ADDITIVE (optional): how this role became available in the org that
  // listed it. 'org' = an org-scoped role owned by that org (orgId === orgId);
  // 'published_global' = a GLOBAL role (orgId IS NULL) surfaced via
  // chaos.role_publications (per-org row OR the all-orgs sentinel). Emitted
  // by GET /api/orgs/:orgId/roles so pickers can distinguish own vs published.
  // Optional so all other producers (CRUD, global-roles, org-chart) stay valid
  // without setting it — never depend on its presence.
  availability: z.enum(['org', 'published_global']).optional(),
  // ── OWNER SIGNATURE (0222) ─────────────────────────────────────────
  // ADDITIVE (optional): WHOSE role this is, so a picker can group by owner and
  // sign each row. Two members of one Workspace may each publish a role named
  // "Analyst"; without this the list shows two identical entries.
  //
  // Emitted by the two LIST routes that have to sign their rows: GET
  // /api/orgs/:orgId/roles (0222, the Workspace picker) and — since 2026-08-12
  // — GET /api/roles/all, whose sharing chooser draws its candidates from the
  // account-wide list and without these three columns put every one of them in
  // the 'unattributed' group. Still optional, so every other producer (CRUD,
  // global-roles, org-chart) stays valid — a consumer must treat `undefined` as
  // "this producer does not say", which is NOT the same as `ownerKind:
  // 'unattributed'` ("this producer says nobody owns it").
  //
  // `email` is null for 'external' owners (someone who published here and is no
  // longer a member) — the display name signs the row, the address is withheld.
  owner: z
    .object({
      userId: z.string().uuid(),
      displayName: z.string(),
      email: z.string().nullable(),
    })
    .nullable()
    .optional(),
  // 'platform' and 'unattributed' are BOTH owner === null and must not be
  // collapsed: the first is 0220's instance set, which belongs to nobody on
  // purpose; the second is a role whose owner could not be derived.
  ownerKind: z.enum(['self', 'member', 'external', 'platform', 'unattributed']).optional(),
});
export type Role = z.infer<typeof roleSchema>;

/**
 * ── THE LIST SHAPE (2026-08-12) ────────────────────────────────────────────
 *
 * What `GET /api/orgs/:orgId/roles` returns. It is `roleSchema` MINUS the
 * fields a list has no business carrying — the role's prompt, its capability
 * surface, and its authority over other people's work.
 *
 * The reason is a measurement, not a preference: on this installation a plain
 * member of one shared Workspace could pull 64,594 characters of
 * `systemPrompt` + `instructions` across 12 roles out of that one endpoint,
 * because the route ran `SELECT r.*` through the full role mapper. Roles belong
 * to an ACCOUNT (founder, 2026-08-02); a picker is not a place to read one.
 *
 * WHY `.omit()` AND NOT A HAND-WRITTEN OBJECT: the list shape must move with
 * `roleSchema` whenever a presentational field is added to it, and must NEVER
 * silently regain one of the eight named here. `.omit` gives both — a new field
 * on `roleSchema` appears in the list, and the eight cannot come back without
 * this list changing. The PRODUCER side is an allowlist for the opposite reason
 * (see `roleListItemFrom` in the gateway's lib/role-visibility.ts): a new
 * DATABASE column must not reach the wire by default.
 *
 * `RoleListItem` is a structural subset of `Role`, so every `Role` is
 * assignable to it — a consumer typed on the list shape also accepts rows from
 * the one remaining full-fidelity producer, `GET /api/global-roles`
 * (system-founder only), without a cast.
 *
 * `GET /api/roles/all` USED TO BE THE SECOND ONE AND NO LONGER IS (2026-08-12,
 * gateway commit 058bf58). That route is gated on "owner of an account", not on
 * the founder, so while it ran `SELECT r.*` through the full role mapper the
 * second person to own an account received the first person's catalogue —
 * measured on this installation: 18 roles and 69,591 characters of
 * `systemPrompt` + `instructions`, 13 of them the founder's. It is now narrowed
 * BY OWNERSHIP (only the account's own roles, plus roles already published into
 * its Workspaces) and produces `roleListItemSchema` rows through the same
 * `roleListItemFrom` allowlist as the picker, with `ownerOrgId` and
 * `publications` added on top. No prompt and no capability surface leave it.
 *
 * THE FULL ROLE IS STILL REACHABLE at `GET /api/orgs/:orgId/roles/:roleId`,
 * which is the role editor's read and is gated to founder/admin.
 */
export const roleListItemSchema = roleSchema.omit({
  // The intellectual property.
  systemPrompt: true,
  instructions: true,
  // The capability surface — which role runs with bypassPermissions, which
  // holds full VPS access, which tools it may call. A guest reading these has a
  // shopping list.
  permissionMode: true,
  allowedTools: true,
  fullVpsAccess: true,
  defaultAutonomy: true,
  // The role's authority over other people's work.
  viewScope: true,
  canAutoApprove: true,
});
export type RoleListItem = z.infer<typeof roleListItemSchema>;

export const createRoleSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  displayName: z.string().min(1).max(200),
  vfp: z.string().min(1).max(4000),
  kind: roleKindSchema,
  departmentId: z.string().uuid().nullable().optional(),
  cliAgent: cliAgentSchema.nullable().optional(),
  systemPrompt: z.string().max(200_000).nullable().optional(),
  permissionMode: permissionModeSchema.nullable().optional(),
  allowedTools: z.array(z.string().max(120)).nullable().optional(),
  defaultAutonomy: roleAutonomyLevelSchema.nullable().optional(),
  viewScope: roleViewScopeSchema.nullable().optional(),
  fullVpsAccess: z.boolean().optional(),
  // extraMounts removed in 0169 — see the note on `roleSchema` above. Zod
  // strips unknown keys by default, so a stale client that still sends the
  // field gets a role created without it rather than a 400.
  reportsToRoleId: z.string().uuid().nullable().optional(),
  canAutoApprove: z.unknown().nullable().optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
  // M-X12: hex color, validated. Optional on create — defaults to
  // '#64748b' at the DB level.
  textColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/)
    .nullable()
    .optional(),
  // M-X16: optional defaults — undefined = "leave alone", null =
  // "clear preference". Founder edits these in Settings → Roles →
  // Default execution settings.
  defaultVendor: vendorSchema.nullable().optional(),
  defaultModel: z.string().max(120).nullable().optional(),
  defaultEffort: effortSchema.nullable().optional(),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = createRoleSchema.partial();
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

// ── Role publication (publish a global role into an org) ─────────────
export const rolePublicationSchema = z.object({
  globalRoleId: z.string().uuid(),
  orgId: z.string().uuid(),
  departmentId: z.string().uuid().nullable(),
  orgOverrides: z.unknown().nullable(),
  reportsToRoleId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});
export type RolePublication = z.infer<typeof rolePublicationSchema>;

export const createRolePublicationSchema = z.object({
  globalRoleId: z.string().uuid(),
  departmentId: z.string().uuid().nullable().optional(),
  orgOverrides: z.unknown().nullable().optional(),
  reportsToRoleId: z.string().uuid().nullable().optional(),
});
export type CreateRolePublicationInput = z.infer<typeof createRolePublicationSchema>;

export const updateRolePublicationSchema = z.object({
  departmentId: z.string().uuid().nullable().optional(),
  orgOverrides: z.unknown().nullable().optional(),
  reportsToRoleId: z.string().uuid().nullable().optional(),
});
export type UpdateRolePublicationInput = z.infer<typeof updateRolePublicationSchema>;

// ── Role assignment ──────────────────────────────────────────────────
export const roleAssignmentSchema = z.object({
  id: z.string().uuid(),
  roleId: z.string().uuid().nullable(),
  orgId: z.string().uuid(),
  userId: z.string().uuid().nullable(),
  agentLabel: z.string().nullable(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type RoleAssignment = z.infer<typeof roleAssignmentSchema>;

export const createRoleAssignmentSchema = z
  .object({
    roleId: z.string().uuid(),
    userId: z.string().uuid().nullable().optional(),
    agentLabel: z.string().min(1).max(120).nullable().optional(),
  })
  .refine((v) => v.userId !== undefined || v.agentLabel !== undefined, {
    message: 'userId or agentLabel required',
  });
export type CreateRoleAssignmentInput = z.infer<typeof createRoleAssignmentSchema>;

// ── Org chart (tree response from /org-chart endpoint) ───────────────
export const orgChartAssignmentSchema = z.object({
  id: z.string().uuid(),
  roleId: z.string().uuid().nullable(),
  userId: z.string().uuid().nullable(),
  userEmail: z.string().nullable(),
  userDisplayName: z.string().nullable(),
  agentLabel: z.string().nullable(),
  startedAt: z.string().datetime(),
});
export type OrgChartAssignment = z.infer<typeof orgChartAssignmentSchema>;

// THE CHART IS A DRAWING, NOT A DOSSIER (2026-08-12). It was
// `roleSchema.extend(...)`, so `GET /api/orgs/:orgId/org-chart` — a route any
// member of the Workspace may call — returned every role's `systemPrompt` and
// `instructions` in full, exactly like the picker did. Built on the list shape
// it draws the same boxes with the same names and stops shipping the prompts.
export const orgChartRoleSchema = roleListItemSchema.extend({
  assignments: z.array(orgChartAssignmentSchema),
  isPublished: z.boolean().optional(),
  rolePublication: rolePublicationSchema.nullable().optional(),
});
export type OrgChartRole = z.infer<typeof orgChartRoleSchema>;

export const orgChartDepartmentSchema = departmentSchema.extend({
  roles: z.array(orgChartRoleSchema),
  children: z.array(z.lazy((): z.ZodType<OrgChartDepartment> => orgChartDepartmentSchema)).optional(),
});
export type OrgChartDepartment = Department & {
  roles: OrgChartRole[];
  children?: OrgChartDepartment[];
};

export const orgChartDivisionSchema = divisionSchema.extend({
  departments: z.array(orgChartDepartmentSchema),
});
export type OrgChartDivision = z.infer<typeof orgChartDivisionSchema>;

export const orgChartSchema = z.object({
  orgId: z.string().uuid(),
  divisions: z.array(orgChartDivisionSchema),
  unassignedDepartments: z.array(orgChartDepartmentSchema),
});
export type OrgChart = z.infer<typeof orgChartSchema>;
